/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import Header from "../../components/Header";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/solid";
import DeleteModal from "../../components/DeleteModal";
import AddButton from "../../components/AddButton";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteNotificationAsync,
  fetchNotificationsAsync,
  createNotificationAsync,
} from "../../redux/slices/notificationsSlice";
import { RootState } from "../../redux/store";
import SuccessMessage from "../../components/SuccessMessage";
import ErrorMessage from "../../components/ErrorMessage";
import Pagination from "../../components/Pagination";
import Loading from "../../components/Loading";

interface NotificationData {
  id: number;
  title: string;
  content: string;
  date: string;
  time: string;
  repeat: string;
}

const Notifications: React.FC = () => {
  const dispatch = useDispatch();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    items: notifications,
    loading,
    error,
    totalPages,
  } = useSelector((state: RootState) => state.notification);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchNotificationsAsync({ page: currentPage, limit: 10 }) as any);
  }, [dispatch, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getCurrentDateTimeISO = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 1);
    return now.toISOString();
  };

  const navigate = useNavigate();

  const handleAddNotification = () => {
    navigate("/add-notification");
  };

  const handleEdit = (notification: NotificationData) => {
    navigate("/edit-notification", { state: { notification } });
  };

  const handleSubmit = (
    values: { content: string },
    { resetForm }: { resetForm: () => void }
  ) => {
    const isoDateTime = getCurrentDateTimeISO();
    const [date, time] = isoDateTime.split("T");

    const formattedNotification = {
      title: "12REPS",
      content: values.content,
      date,
      time: time.substring(0, 5),
      repeat: "Clear",
    };

    console.log("Sending notification:", formattedNotification);

    dispatch(createNotificationAsync(formattedNotification) as any)
      .unwrap()
      .then(() => {
        setSuccessMessage("Notification sent successfully!");
        resetForm();
      })
      .catch(() => {
        setErrorMessage("Failed to send notification.");
      });
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] =
    useState<NotificationData | null>(null);

  const openDeleteModal = (notification: NotificationData) => {
    setNotificationToDelete(notification);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setNotificationToDelete(null);
  };

  const handleDeleteConfirm = () => {
    if (notificationToDelete) {
      dispatch(deleteNotificationAsync(notificationToDelete.id) as any)
        .unwrap()
        .then(() => {
          setSuccessMessage("Notification deleted successfully!");
        })
        .catch((err: any) => {
          setErrorMessage(err || "Failed to delete notification.");
        });
      closeDeleteModal();
    }
  };

  const validationSchema = Yup.object({
    content: Yup.string()
      .required("Notification content is required")
      .max(1000, "Maximum 1000 characters allowed"),
  });

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <Header title="Push Notifications" />
      {loading ? (
        <Loading />
      ) : (
        <>
          <div className="mt-4 mb-4 flex justify-end">
            <AddButton
              text="Add Notification"
              onClick={handleAddNotification}
            />
          </div>
          {successMessage && (
            <SuccessMessage
              message={successMessage}
              onClose={() => setSuccessMessage(null)}
            />
          )}
          {errorMessage && (
            <ErrorMessage
              message={errorMessage}
              onClose={() => setErrorMessage(null)}
            />
          )}
          <Formik
            initialValues={{ content: "" }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched }) => (
              <Form className="bg-white p-4 rounded-md shadow mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Notification Content
                </label>
                <Field
                  name="content"
                  as="textarea"
                  maxLength={1000}
                  className="w-full p-2 border rounded-md focus:ring focus:ring-blue-300"
                  placeholder="Enter notification content"
                />
                {errors.content && touched.content && (
                  <div className="text-red-500 text-sm mt-1">
                    {errors.content}
                  </div>
                )}
                <button
                  type="submit"
                  className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition"
                >
                  Send push notification
                </button>
              </Form>
            )}
          </Formik>

          <div className="bg-white rounded-md shadow overflow-auto">
            <table className="min-w-full table-auto">
              <thead className="bg-gray-200">
                <tr>
                  <th className="py-2 px-4 text-left">ID</th>
                  <th className="py-2 px-4 text-left">Notification Title</th>
                  <th className="py-2 px-4 text-left">Notification Content</th>
                  <th className="py-2 px-4 text-left">Date</th>
                  <th className="py-2 px-4 text-left">Time</th>
                  <th className="py-2 px-4 text-left">Repeat</th>
                  <th className="py-2 px-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {notifications.map((notification) => (
                  <tr key={notification.id} className="border-t">
                    <td className="py-2 px-4">{notification.id}</td>
                    <td className="py-2 px-4">{notification.title}</td>
                    <td className="py-2 px-4">{notification.content}</td>
                    <td className="py-2 px-4">{notification.date}</td>
                    <td className="py-2 px-4">{notification.time}</td>
                    <td className="py-2 px-4">{notification.repeat}</td>
                    <td className="py-2 px-4 flex gap-2">
                      <button
                        onClick={() => handleEdit(notification)}
                        className="px-2 py-1 mr-2 text-yellow-600 border border-yellow-600 rounded hover:bg-gray-100"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => openDeleteModal(notification)}
                        className="px-2 py-1 text-red-600 border border-red-600 rounded hover:bg-gray-100"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
          <DeleteModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onConfirm={handleDeleteConfirm}
            itemName={notificationToDelete?.title || ""}
            itemType="notification"
          />
        </>
      )}
    </div>
  );
};

export default Notifications;
